import React, { useState, useEffect } from 'react';
import { Home, Plus, Trash2, Calendar, Link, Edit2, Save, X, Sparkles, Clock, MapPin, AlertCircle, BookOpen, FileText } from 'lucide-react';
import useLocalStorage from './hooks/useLocalStorage';
import { getDaysUntil, getDateLabel, getUrgencyColor, getPriorityColor } from './utils';

const MAX_TEXT  = 200;
const MAX_SHORT = 100;
const MAX_NOTES = 2000;
const MAX_URL   = 500;

const sanitize      = (str, max = MAX_TEXT)  => String(str ?? '').replace(/[<>"'`]/g, '').slice(0, max);
const sanitizeUrl   = (url) => { try { const u = new URL(url.trim()); return (u.protocol === 'https:' || u.protocol === 'http:') ? u.href : ''; } catch { return ''; } };

// ── Schedule ──────────────────────────────────────────────────

const Schedule = ({ setCurrentModule }) => {
  const [filter,      setFilter]      = useState('upcoming');
  const [celebrateId, setCelebrateId] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [showModal,   setShowModal]   = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Migration-aware load: useLocalStorage can't read from legacy keys on first run
  const [items, setItems] = useLocalStorage('scheduleItems', null);

  useEffect(() => {
    if (items !== null) return;
    const old  = JSON.parse(localStorage.getItem('assignments')  || '[]');
    const oldE = JSON.parse(localStorage.getItem('eventsExams')  || '[]');
    const migrated = [
      ...old.map(a => ({ ...a, type: 'assignment', date: a.deadline })),
      ...oldE,
    ];
    setItems(migrated);
  }, []);

  useEffect(() => {
    if (!celebrateId) return;
    const t = setTimeout(() => setCelebrateId(null), 3000);
    return () => clearTimeout(t);
  }, [celebrateId]);

  if (items === null) {
    return (
      <div className="page flex items-center justify-center">
        <div className="text-center">
          <Calendar className="w-12 h-12 text-neutral-400 mx-auto mb-4 border-4 border-neutral-900 p-2" />
          <div className="text-neutral-900 font-mono font-bold uppercase">Loading schedule...</div>
        </div>
      </div>
    );
  }

  // Item actions
  const createNewItem = (type) => {
    const base = { id: Date.now(), type, title: '', date: '', course: '', notes: '', recurring: false, recurrenceType: 'none', recurrenceEnd: '', originalDate: '' };
    setEditingItem(type === 'assignment'
      ? { ...base, priority: 'medium', status: 'not-started', progress: 0, subtasks: [], links: [] }
      : { ...base, time: '', location: '' }
    );
    setShowModal(true);
  };

  const saveItem = (item) => {
    const clean = {
      ...item,
      title:    sanitize(item.title, MAX_SHORT),
      course:   sanitize(item.course, MAX_SHORT),
      notes:    sanitize(item.notes, MAX_NOTES),
      location: sanitize(item.location, MAX_SHORT),
    };
    setItems(prev => (prev || []).find(i => i.id === clean.id) ? (prev || []).map(i => i.id === clean.id ? clean : i) : [...(prev || []), clean]);
    setShowModal(false);
    setEditingItem(null);
  };

  const confirmDelete = (id) => {
    const item = items.find(i => i.id === id);
    if (item?.isRecurringInstance) {
      setDeleteConfirm({ id: item.parentId, message: 'Delete all occurrences of this recurring event?' });
    } else {
      setDeleteConfirm({ id, message: 'Delete this item?' });
    }
  };

  const doDelete = () => {
    setItems(items.filter(i => i.id !== deleteConfirm.id));
    setDeleteConfirm(null);
  };

  const toggleSubtask = (itemId, idx) => {
    setItems(items.map(item => {
      if (item.id !== itemId || item.type !== 'assignment') return item;
      const subtasks = [...item.subtasks];
      subtasks[idx] = { ...subtasks[idx], completed: !subtasks[idx].completed };
      const progress = subtasks.length > 0 ? Math.round(subtasks.filter(s => s.completed).length / subtasks.length * 100) : 0;
      return { ...item, subtasks, progress };
    }));
  };

  const updateStatus = (itemId, status) => {
    setItems(items.map(i => i.id === itemId ? { ...i, status } : i));
    if (status === 'completed') setCelebrateId(itemId);
  };

  // Recurring expansion
  const generateRecurringInstances = () => {
    const instances = [];
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const oneYear = new Date(today); oneYear.setFullYear(oneYear.getFullYear() + 1);

    items.forEach(item => {
      if (!item.recurring || !item.date || item.recurrenceType === 'none') {
        instances.push(item); return;
      }
      const orig    = new Date(item.originalDate || item.date);
      const endDate = item.recurrenceEnd ? new Date(item.recurrenceEnd) : oneYear;
      let cur = new Date(orig);

      while (cur <= endDate) {
        if (item.recurrenceType === 'yearly' && orig.getMonth() === 1 && orig.getDate() === 29) {
          const y = cur.getFullYear();
          if (!((y % 4 === 0 && y % 100 !== 0) || y % 400 === 0)) cur.setDate(28);
        }
        if (item.recurrenceType === 'monthly') {
          const target = orig.getDate();
          const daysInMonth = new Date(cur.getFullYear(), cur.getMonth() + 1, 0).getDate();
          if (target > daysInMonth) cur.setDate(daysInMonth);
        }
        instances.push({ ...item, id: `${item.id}-${cur.toISOString().split('T')[0]}`, date: cur.toISOString().split('T')[0], isRecurringInstance: true, parentId: item.id });
        if      (item.recurrenceType === 'daily')   cur.setDate(cur.getDate() + 1);
        else if (item.recurrenceType === 'weekly')  cur.setDate(cur.getDate() + 7);
        else if (item.recurrenceType === 'monthly') { cur.setMonth(cur.getMonth() + 1); cur.setDate(orig.getDate()); }
        else if (item.recurrenceType === 'yearly')  { cur.setFullYear(cur.getFullYear() + 1); cur.setMonth(orig.getMonth()); cur.setDate(orig.getDate()); }
      }
    });
    return instances;
  };

  const filterItems = () => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return generateRecurringInstances()
      .filter(item => {
        if (filter === 'upcoming')    return item.date && !(item.type === 'assignment' && item.status === 'completed') && new Date(item.date) >= today;
        if (filter === 'assignments') return item.type === 'assignment' && item.status !== 'completed';
        if (filter === 'exams')       return item.type === 'exam';
        if (filter === 'events')      return item.type === 'event';
        if (filter === 'completed')   return item.type === 'assignment' && item.status === 'completed';
        if (filter === 'past')        return item.date && item.type !== 'assignment' && new Date(item.date) < today;
        return true;
      })
      .sort((a, b) => {
        if (!a.date) return 1; if (!b.date) return -1;
        return new Date(a.date) - new Date(b.date);
      });
  };

  const filteredItems = filterItems();
  const counts = {
    upcoming:    items.filter(i => i.date && !(i.type === 'assignment' && i.status === 'completed') && new Date(i.date) >= new Date()).length,
    assignments: items.filter(i => i.type === 'assignment' && i.status !== 'completed').length,
    exams:       items.filter(i => i.type === 'exam').length,
    events:      items.filter(i => i.type === 'event').length,
    completed:   items.filter(i => i.type === 'assignment' && i.status === 'completed').length,
  };

  const TABS = [
    { id: 'upcoming',    label: `Upcoming (${counts.upcoming})` },
    { id: 'all',         label: `All (${items.length})` },
    { id: 'assignments', label: `Assignments (${counts.assignments})` },
    { id: 'exams',       label: `Exams (${counts.exams})` },
    { id: 'events',      label: `Events (${counts.events})` },
    { id: 'completed',   label: `Completed (${counts.completed})` },
    { id: 'past',        label: 'Past' },
  ];

  const STATUS_CONFIGS = {
    'not-started': { label: 'Not Started', bgColor: 'bg-white',     hoverBg: 'hover:bg-neutral-100' },
    'in-progress':  { label: 'In Progress', bgColor: 'bg-blue-400',  hoverBg: 'hover:bg-blue-500' },
    'completed':    { label: 'Completed',   bgColor: 'bg-green-400', hoverBg: 'hover:bg-green-500' },
  };

  const TYPE_BADGES = {
    assignment: <div className="bg-red-400 border-2 border-neutral-900 text-neutral-900 px-3 py-1 text-xs uppercase tracking-wider font-bold font-mono">📝 Assignment</div>,
    exam:       <div className="bg-orange-400 border-2 border-neutral-900 text-neutral-900 px-3 py-1 text-xs uppercase tracking-wider font-bold font-mono">📚 Exam</div>,
    event:      <div className="bg-blue-400 border-2 border-neutral-900 text-neutral-900 px-3 py-1 text-xs uppercase tracking-wider font-bold font-mono">📅 Event</div>,
  };

  return (
    <div className="page">
      <div className="page-content-lg">
        <button onClick={() => setCurrentModule('dashboard')} className="btn-back">
          <Home className="w-4 h-4" /><span>Dashboard</span>
        </button>

        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <h1 className="page-title">SCHEDULE</h1>
            <Calendar className="w-6 h-6 text-neutral-900" />
          </div>
          <p className="page-subtitle">Assignments, exams & events</p>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          {[
            { type: 'assignment', Icon: FileText,  label: 'Assignment', primary: true },
            { type: 'exam',       Icon: BookOpen,  label: 'Exam',       primary: false },
            { type: 'event',      Icon: Calendar,  label: 'Event',      primary: false },
          ].map(({ type, Icon, label, primary }) => (
            <button
              key={type}
              onClick={() => createNewItem(type)}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 sm:py-4 text-sm uppercase tracking-wider font-bold font-mono border-2 border-neutral-900 transition-all ${
                primary
                  ? 'bg-neutral-900 text-white hover:bg-neutral-700 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] hover-lift-xl'
                  : 'bg-white text-neutral-900 hover-lift-lg shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]'
              }`}
            >
              <Icon className="w-5 h-5" /> {label}
            </button>
          ))}
        </div>

        {/* Filter tabs */}
        <div className="card-md bg-white mb-6 overflow-x-auto">
          <div className="flex min-w-max">
            {TABS.map((tab, i) => (
              <button
                key={tab.id}
                onClick={() => setFilter(tab.id)}
                className={`flex-1 px-4 py-3 sm:py-4 text-xs sm:text-sm font-mono font-bold uppercase tracking-wider transition-all whitespace-nowrap ${i < TABS.length - 1 ? 'border-r-2 border-neutral-900' : ''} ${filter === tab.id ? 'tab-active' : 'tab-inactive'}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        {filteredItems.length === 0 ? (
          <div className="card-empty bg-white p-8 sm:p-12 lg:p-16 text-center shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]">
            <Calendar className="w-12 h-12 sm:w-16 sm:h-16 text-neutral-400 mx-auto mb-4 border-4 border-neutral-900 p-2" />
            <h3 className="text-lg sm:text-xl font-serif text-neutral-900 mb-2 uppercase">Nothing here yet</h3>
            <p className="text-sm sm:text-base text-neutral-600 mb-6 font-mono">Start by adding an assignment, exam, or event</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button onClick={() => createNewItem('assignment')} className="bg-neutral-900 text-white px-6 py-3 text-sm uppercase tracking-wider font-bold hover:bg-neutral-700 card-sm hover-lift-lg font-mono">Add Assignment</button>
              <button onClick={() => createNewItem('exam')}       className="border-2 border-neutral-900 bg-white text-neutral-900 px-6 py-3 text-sm uppercase tracking-wider font-bold hover-lift font-mono">Add Exam</button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredItems.map(item => {
              const daysUntil    = getDaysUntil(item.date);
              const dateLabel    = getDateLabel(daysUntil);
              const urgencyColor = getUrgencyColor(daysUntil, item.type, item.status);
              const isCelebrating = celebrateId === item.id;

              return (
                <div key={item.id} className={`border-4 ${urgencyColor} hover-lift-xl duration-200 relative overflow-hidden shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] ${isCelebrating ? 'animate-pulse' : ''}`}>
                  {isCelebrating && (
                    <div className="absolute inset-0 bg-gradient-to-r from-green-200 via-emerald-200 to-green-200 opacity-50 animate-pulse pointer-events-none z-10">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Sparkles className="w-16 h-16 text-green-600 animate-spin" />
                      </div>
                    </div>
                  )}

                  <div className="p-4 sm:p-6 relative z-0">
                    <div className="flex items-start justify-between mb-4 gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          {TYPE_BADGES[item.type]}
                          {dateLabel && item.status !== 'completed' && (
                            <span className={`text-xs sm:text-sm font-bold whitespace-nowrap font-mono ${
                              daysUntil < 0 ? 'text-red-600' : daysUntil === 0 ? 'text-red-600' : daysUntil <= 3 ? 'text-orange-600' : daysUntil <= 7 ? 'text-yellow-600' : 'text-neutral-900'
                            }`}>
                              {dateLabel}
                            </span>
                          )}
                        </div>

                        <h3 className="text-xl sm:text-2xl font-serif text-neutral-900 mb-2 break-words uppercase">{item.title || 'Untitled'}</h3>

                        <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-neutral-900 font-mono">
                          {item.course   && <span className="font-bold uppercase">{item.course}</span>}
                          {item.date     && <span className="flex items-center gap-1"><Calendar className="w-3 h-3 sm:w-4 sm:h-4" />{new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>}
                          {item.time     && <span className="flex items-center gap-1"><Clock className="w-3 h-3 sm:w-4 sm:h-4" />{item.time}</span>}
                          {item.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3 sm:w-4 sm:h-4" />{item.location}</span>}
                          {item.type === 'assignment' && item.priority && <span className={`capitalize font-bold ${getPriorityColor(item.priority)}`}>{item.priority} priority</span>}
                        </div>
                      </div>

                      <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                        <button
                          onClick={() => {
                            const target = item.isRecurringInstance ? items.find(i => i.id === item.parentId) : item;
                            if (target) { setEditingItem(target); setShowModal(true); }
                          }}
                          className="btn-icon"
                        >
                          <Edit2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        </button>
                        <button onClick={() => confirmDelete(item.id)} className="btn-icon-danger">
                          <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        </button>
                      </div>
                    </div>

                    {item.type === 'assignment' && (
                      <>
                        {item.subtasks?.length > 0 && (
                          <div className="mb-4">
                            <div className="flex items-center justify-between text-xs sm:text-sm text-neutral-900 mb-2 font-mono font-bold">
                              <span className="uppercase">Progress</span><span>{item.progress || 0}%</span>
                            </div>
                            <div className="progress-track"><div className="progress-fill" style={{ width: `${item.progress || 0}%` }} /></div>
                          </div>
                        )}

                        {item.subtasks?.length > 0 && (
                          <div className="space-y-2 mb-4">
                            {item.subtasks.map((subtask, idx) => (
                              <label key={idx} className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-white border-2 border-neutral-900 hover:bg-neutral-100 cursor-pointer transition-colors">
                                <div className="relative">
                                  <input type="checkbox" checked={subtask.completed} onChange={() => toggleSubtask(item.id, idx)} className="w-5 h-5 border-2 border-neutral-900 appearance-none checked:bg-neutral-900 cursor-pointer" />
                                  {subtask.completed && <svg className="w-5 h-5 text-white absolute top-0 left-0 pointer-events-none" viewBox="0 0 16 16" fill="none"><path d="M3 8L6 11L13 4" stroke="currentColor" strokeWidth="3" /></svg>}
                                </div>
                                <span className={`text-xs sm:text-sm flex-1 break-words font-mono ${subtask.completed ? 'line-through text-neutral-500' : 'text-neutral-900 font-bold'}`}>{subtask.text}</span>
                              </label>
                            ))}
                          </div>
                        )}

                        {item.links?.length > 0 && (
                          <div className="border-t-2 border-neutral-900 pt-4 mb-4">
                            <div className="text-xs uppercase tracking-wider text-neutral-900 mb-2 font-mono font-bold">Resources</div>
                            <div className="space-y-1">
                              {item.links.map((link, idx) => (
                                <a key={idx} href={link.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-xs sm:text-sm text-blue-600 hover:text-blue-800 hover:underline break-all font-mono font-bold">
                                  <Link className="w-3 h-3 flex-shrink-0" />{link.label || link.url}
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {item.notes && (
                      <div className="border-t-2 border-neutral-900 pt-4 mt-4">
                        <div className="text-xs uppercase tracking-wider text-neutral-900 mb-2 font-mono font-bold">Notes</div>
                        <p className="text-xs sm:text-sm text-neutral-700 whitespace-pre-wrap break-words font-mono">{item.notes}</p>
                      </div>
                    )}

                    {item.type === 'exam' && daysUntil !== null && daysUntil >= 0 && daysUntil <= 14 && (
                      <div className="border-t-2 border-neutral-900 pt-4 mt-4">
                        <div className="flex items-start gap-3 bg-yellow-100 border-2 border-yellow-600 p-3">
                          <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                          <div className="text-sm text-yellow-900 font-mono font-bold">
                            EXAM REMINDER: {daysUntil === 0 ? 'TODAY!' : `${daysUntil} DAY${daysUntil !== 1 ? 'S' : ''} TO PREPARE`}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {item.type === 'assignment' && (
                    <div className="border-t-4 border-neutral-900 px-4 sm:px-6 py-3 bg-neutral-50">
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(STATUS_CONFIGS).map(([status, config]) => {
                          const isActive = item.status === status;
                          return (
                            <button
                              key={status}
                              onClick={() => updateStatus(item.id, status)}
                              className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-bold transition-all border-2 border-neutral-900 font-mono uppercase tracking-wider ${
                                isActive
                                  ? `${config.bgColor} text-neutral-900 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]`
                                  : `bg-white text-neutral-500 ${config.hoverBg} hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:-translate-x-0.5 hover:-translate-y-0.5`
                              }`}
                            >
                              {status === 'completed' && isActive && '🎉 '}{config.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Delete confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50">
          <div className="bg-white border-4 border-neutral-900 max-w-sm w-full shadow-[12px_12px_0px_0px_rgba(0,0,0,1)] p-6">
            <h3 className="text-xl font-serif text-neutral-900 uppercase mb-2">Delete?</h3>
            <p className="text-sm font-mono text-neutral-600 mb-6">{deleteConfirm.message}</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 border-2 border-neutral-900 py-3 text-sm uppercase font-bold font-mono hover-lift">Cancel</button>
              <button onClick={doDelete} className="flex-1 bg-red-600 text-white py-3 text-sm uppercase font-bold font-mono border-2 border-neutral-900 hover:bg-red-700 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">Delete</button>
            </div>
          </div>
        </div>
      )}

      {showModal && editingItem && (
        <ItemModal item={editingItem} setItem={setEditingItem} onSave={saveItem} onCancel={() => { setShowModal(false); setEditingItem(null); }} />
      )}
    </div>
  );
};

// ── ItemModal ─────────────────────────────────────────────────

const ItemModal = ({ item, setItem, onSave, onCancel }) => {
  const [newSubtask,   setNewSubtask]   = useState('');
  const [newLinkLabel, setNewLinkLabel] = useState('');
  const [newLinkUrl,   setNewLinkUrl]   = useState('');
  const [error,        setError]        = useState(null);

  const isAssignment = item.type === 'assignment';
  const isExam       = item.type === 'exam';
  const isEvent      = item.type === 'event';

  const handleSave = () => {
    if (!sanitize(item.title, MAX_SHORT).trim()) { setError('Title is required'); return; }
    if (!item.date) { setError('Date is required'); return; }
    setError(null);
    onSave(item);
  };

  const addSubtask = () => {
    const text = sanitize(newSubtask);
    if (!text.trim()) return;
    setItem({ ...item, subtasks: [...(item.subtasks || []), { text, completed: false }] });
    setNewSubtask('');
  };

  const addLink = () => {
    const url = sanitizeUrl(newLinkUrl);
    if (!url) { setError('Enter a valid https:// URL'); return; }
    setError(null);
    setItem({ ...item, links: [...(item.links || []), { label: sanitize(newLinkLabel, MAX_SHORT) || url, url }] });
    setNewLinkLabel(''); setNewLinkUrl('');
  };

  const TYPE_BADGE_MODAL = {
    assignment: <div className="bg-red-400 border-2 border-neutral-900 text-neutral-900 px-3 py-2 text-xs uppercase tracking-wider font-bold font-mono">📝 Assignment</div>,
    exam:       <div className="bg-orange-400 border-2 border-neutral-900 text-neutral-900 px-3 py-2 text-xs uppercase tracking-wider font-bold font-mono">📚 Exam</div>,
    event:      <div className="bg-blue-400 border-2 border-neutral-900 text-neutral-900 px-3 py-2 text-xs uppercase tracking-wider font-bold font-mono">📅 Event</div>,
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 sm:p-8 z-50 overflow-y-auto">
      <div className="bg-white border-4 border-neutral-900 max-w-2xl w-full shadow-[12px_12px_0px_0px_rgba(0,0,0,1)] max-h-[90vh] overflow-y-auto my-auto">
        <div className="border-b-4 border-neutral-900 p-4 sm:p-6 flex items-center justify-between sticky top-0 bg-neutral-900 z-10">
          <h2 className="modal-title">{item.title ? `Edit ${item.type}` : `New ${item.type}`}</h2>
          <button onClick={onCancel} className="text-white hover:bg-neutral-700 transition-colors border-2 border-white p-1"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          {error && <div className="bg-red-50 border-2 border-red-600 p-3 text-red-700 text-sm font-mono flex items-center gap-2"><AlertCircle className="w-4 h-4 flex-shrink-0"/>{error}</div>}

          <div>{TYPE_BADGE_MODAL[item.type]}</div>

          <div>
            <label className="label mb-2">{isAssignment ? 'Assignment Title' : isExam ? 'Exam Name' : 'Event Name'} *</label>
            <input type="text" value={item.title} maxLength={MAX_SHORT}
              onChange={e => { setError(null); setItem({ ...item, title: e.target.value }); }}
              placeholder={isAssignment ? 'e.g., Research Paper' : isExam ? 'e.g., Midterm Exam' : 'e.g., Career Fair'}
              className="w-full border-4 border-neutral-900 px-3 py-3 text-sm sm:text-base text-neutral-900 focus:outline-none focus:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-all font-mono font-bold"
              autoFocus
            />
          </div>

          <div>
            <label className="label mb-2">Course/Subject</label>
            <input type="text" value={item.course || ''} maxLength={MAX_SHORT}
              onChange={e => setItem({ ...item, course: e.target.value })}
              placeholder="e.g., CS 101, Biology"
              className="w-full border-4 border-neutral-900 px-3 py-3 text-sm sm:text-base text-neutral-900 focus:outline-none focus:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-all font-mono"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label mb-2">{isAssignment ? 'Deadline' : 'Date'} *</label>
              <input type="date" value={item.date}
                onChange={e => { setError(null); setItem({ ...item, date: e.target.value }); }}
                className="w-full border-4 border-neutral-900 px-3 py-3 text-sm sm:text-base text-neutral-900 focus:outline-none focus:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-all font-mono font-bold"
              />
            </div>
            <div>
              <label className="label mb-2">Recurring</label>
              <label className="flex items-center gap-2 cursor-pointer h-[50px] border-4 border-neutral-900 px-3 bg-white">
                <div className="relative flex-shrink-0">
                  <input type="checkbox" checked={item.recurring || false}
                    onChange={e => setItem({ ...item, recurring: e.target.checked, recurrenceType: e.target.checked ? 'monthly' : 'none', originalDate: item.date || '' })}
                    className="w-5 h-5 border-2 border-neutral-900 appearance-none checked:bg-neutral-900 cursor-pointer"
                  />
                  {item.recurring && <svg className="w-5 h-5 text-white absolute inset-0 pointer-events-none" viewBox="0 0 16 16" fill="none"><path d="M3 8L6 11L13 4" stroke="currentColor" strokeWidth="3" /></svg>}
                </div>
                <span className="text-sm font-mono font-bold text-neutral-900">RECURRING EVENT</span>
              </label>
            </div>
          </div>

          {item.recurring && (
            <div className="space-y-4">
              <div className="border-2 border-neutral-900 p-4 bg-yellow-50">
                <label className="label mb-2">Repeat</label>
                <div className="flex gap-4">
                  {[{ value: 'monthly', label: 'Monthly' }, { value: 'yearly', label: 'Yearly' }].map(opt => (
                    <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                      <div className="relative flex items-center">
                        <input type="radio" name="recurrence" value={opt.value} checked={item.recurrenceType === opt.value} onChange={e => setItem({ ...item, recurrenceType: e.target.value })} className="w-5 h-5 border-2 border-neutral-900 appearance-none checked:bg-neutral-900 cursor-pointer" />
                        {item.recurrenceType === opt.value && <div className="w-3 h-3 bg-white absolute inset-0 m-auto pointer-events-none" />}
                      </div>
                      <span className="text-xs sm:text-sm font-mono font-bold">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="border-2 border-neutral-900 p-4 bg-yellow-50">
                <label className="label mb-2">End Date (Optional)</label>
                <input type="date" value={item.recurrenceEnd || ''} min={item.date} onChange={e => setItem({ ...item, recurrenceEnd: e.target.value })} className="w-full border-4 border-neutral-900 px-3 py-3 text-sm sm:text-base text-neutral-900 focus:outline-none font-mono font-bold" />
                <p className="text-xs text-neutral-600 mt-2 font-mono">Leave empty to repeat for 1 year</p>
              </div>
              {item.recurrenceType === 'monthly' && item.date && new Date(item.date).getDate() > 28 && (
                <div className="flex items-start gap-2 bg-yellow-100 border-2 border-yellow-600 p-3">
                  <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-yellow-900 font-mono">On months with fewer days, this will occur on the last day.</p>
                </div>
              )}
              {item.recurrenceType === 'yearly' && item.date && new Date(item.date).getMonth() === 1 && new Date(item.date).getDate() === 29 && (
                <div className="flex items-start gap-2 bg-yellow-100 border-2 border-yellow-600 p-3">
                  <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-yellow-900 font-mono">On non-leap years, this will occur on February 28th.</p>
                </div>
              )}
            </div>
          )}

          {(isExam || isEvent) && (
            <div>
              <label className="label mb-2">Time</label>
              <input type="time" value={item.time || ''} onChange={e => setItem({ ...item, time: e.target.value })} className="w-full border-4 border-neutral-900 px-3 py-3 text-sm sm:text-base text-neutral-900 focus:outline-none font-mono font-bold" />
            </div>
          )}

          {(isExam || isEvent) && (
            <div>
              <label className="label mb-2">Location</label>
              <input type="text" value={item.location || ''} maxLength={MAX_SHORT}
                onChange={e => setItem({ ...item, location: e.target.value })}
                placeholder={isExam ? 'e.g., Room 301' : 'e.g., Student Center'}
                className="w-full border-4 border-neutral-900 px-3 py-3 text-sm sm:text-base text-neutral-900 focus:outline-none font-mono"
              />
            </div>
          )}

          {isAssignment && (
            <div>
              <label className="label mb-2">Priority</label>
              <div className="flex flex-wrap gap-3 sm:gap-4">
                {['low', 'medium', 'high'].map(p => (
                  <label key={p} className="flex items-center gap-2 cursor-pointer">
                    <div className="relative">
                      <input type="radio" name="priority" value={p} checked={item.priority === p} onChange={e => setItem({ ...item, priority: e.target.value })} className="w-5 h-5 border-2 border-neutral-900 appearance-none checked:bg-neutral-900 cursor-pointer" />
                      {item.priority === p && <div className="w-3 h-3 bg-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none" />}
                    </div>
                    <span className="text-xs sm:text-sm capitalize font-mono font-bold">{p}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {isAssignment && (
            <div>
              <label className="label mb-2">Subtasks / Checklist</label>
              <div className="space-y-2 mb-3">
                {item.subtasks?.map((subtask, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-2 bg-white border-2 border-neutral-900">
                    <span className="text-xs sm:text-sm flex-1 text-neutral-900 break-words font-mono">{subtask.text}</span>
                    <button onClick={() => setItem({ ...item, subtasks: item.subtasks.filter((_, i) => i !== idx) })} className="btn-icon-danger p-1"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <input type="text" value={newSubtask} maxLength={MAX_TEXT}
                  onChange={e => setNewSubtask(e.target.value)}
                  onKeyPress={e => e.key === 'Enter' && addSubtask()}
                  placeholder="Add a subtask..."
                  className="flex-1 border-4 border-neutral-900 px-3 py-2 text-xs sm:text-sm text-neutral-900 focus:outline-none focus:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] font-mono"
                />
                <button onClick={addSubtask} className="px-3 sm:px-4 py-2 bg-neutral-900 text-white hover:bg-neutral-700 transition-colors flex-shrink-0 border-2 border-neutral-900"><Plus className="w-4 h-4" /></button>
              </div>
            </div>
          )}

          {isAssignment && (
            <div>
              <label className="label mb-2">Links & Resources</label>
              <div className="space-y-2 mb-3">
                {item.links?.map((link, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-2 bg-white border-2 border-neutral-900">
                    <Link className="w-4 h-4 text-neutral-900 flex-shrink-0" />
                    <div className="flex-1 text-xs sm:text-sm min-w-0">
                      <div className="text-neutral-900 break-words font-mono font-bold">{link.label}</div>
                      <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs break-all font-mono">{link.url}</a>
                    </div>
                    <button onClick={() => setItem({ ...item, links: item.links.filter((_, i) => i !== idx) })} className="btn-icon-danger p-1"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                <input type="text" value={newLinkLabel} maxLength={MAX_SHORT} onChange={e => setNewLinkLabel(e.target.value)} placeholder="Label (optional)" className="w-full border-4 border-neutral-900 px-3 py-2 text-xs sm:text-sm text-neutral-900 focus:outline-none font-mono" />
                <div className="flex gap-2">
                  <input type="url" value={newLinkUrl} maxLength={MAX_URL}
                    onChange={e => { setError(null); setNewLinkUrl(e.target.value); }}
                    onKeyPress={e => e.key === 'Enter' && addLink()}
                    placeholder="https://..."
                    className="flex-1 border-4 border-neutral-900 px-3 py-2 text-xs sm:text-sm text-neutral-900 focus:outline-none font-mono"
                  />
                  <button onClick={addLink} className="px-3 sm:px-4 py-2 bg-neutral-900 text-white hover:bg-neutral-700 transition-colors flex-shrink-0 border-2 border-neutral-900"><Plus className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="label mb-2">Notes</label>
            <textarea value={item.notes} maxLength={MAX_NOTES}
              onChange={e => setItem({ ...item, notes: e.target.value })}
              placeholder={isAssignment ? 'Additional details...' : isExam ? 'Topics covered, materials allowed...' : 'Details, dress code...'}
              rows={4}
              className="w-full border-4 border-neutral-900 px-3 py-2 text-xs sm:text-sm text-neutral-900 focus:outline-none focus:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-all font-mono"
            />
          </div>
        </div>

        <div className="border-t-4 border-neutral-900 p-4 sm:p-6 flex flex-col sm:flex-row gap-3 sm:gap-4 sticky bottom-0 bg-white">
          <button onClick={onCancel} className="flex-1 border-2 border-neutral-900 bg-white text-neutral-900 py-2 sm:py-3 text-xs sm:text-sm uppercase tracking-wider font-bold hover-lift font-mono">Cancel</button>
          <button onClick={handleSave} className="flex-1 bg-neutral-900 text-white py-2 sm:py-3 text-xs sm:text-sm uppercase tracking-wider font-bold hover:bg-neutral-700 transition-all flex items-center justify-center gap-2 card-sm hover-lift-lg font-mono">
            <Save className="w-4 h-4" /> Save {isAssignment ? 'Assignment' : isExam ? 'Exam' : 'Event'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Schedule;